<template>
    <form novalidate @submit.prevent="handleSubmit">
        <div>
            <ds-input-field
                ref="nameInput"
                v-model="name"
                name="name"
                :label="$t('global.name')"
                required
                :submitted="isSubmitted"
            >
                <template #error>
                    {{ $t('unlayerBuilder.saveAsTemplate.error.thisFieldRequired') }}
                </template>
            </ds-input-field>
        </div>

        <div v-if="containsCampaignFieldsOrLinks" class="fields-links-alert">
            <ds-inline-alert>
                {{ $t(campaignReferencesAlertDescriptionCode) }}
            </ds-inline-alert>
        </div>

        <div>
            <ds-filled-button type="submit" :loading="isSaving">
                {{ $t('global.save') }}
            </ds-filled-button>
        </div>

        <div v-if="isError" class="alert-container">
            <ds-inline-alert type="critical" leading-icon>
                There was an error saving your template.
            </ds-inline-alert>
        </div>
    </form>
</template>

<script>
import axios from 'axios';
import sentry from '@/analytics/sentry';


const STATUS_UNSUBMITTED = 'UNSUBMITTED';
const STATUS_SUBMITTED = 'SUBMITTED';
const STATUS_SAVING = 'SAVING';
const STATUS_ERROR = 'ERROR';

export default {
    props: {
        editor: Object,
        funnelId: String,
        templateId: String,
    },

    data() {
        return {
            name: '',
            status: STATUS_UNSUBMITTED,
            html: '',
        };
    },

    mounted() {
        this.focusNameInput();
        this.editor.exportHtml(({ html }) => {
            this.html = html;
        });
    },

    computed: {
        isSaving() {
            return this.status === STATUS_SAVING;
        },

        isSubmitted() {
            return this.status !== STATUS_UNSUBMITTED;
        },

        isError() {
            return this.status === STATUS_ERROR;
        },

        containsCampaignFields() {
            // eslint-disable-next-line
            return this.html.match('~Campaign\..*_[^~]*~');
        },

        containsCampaignLinks() {
            return this.html.match('data-campaign-link-id=');
        },

        containsCampaignFieldsOrLinks() {
            return this.containsCampaignFields || this.containsCampaignLinks;
        },

        campaignReferencesAlertDescriptionCode() {
            if (this.containsCampaignFields && this.containsCampaignLinks) {
                return 'unlayerBuilder.saveAsTemplate.alert.campaign.fields.and.links';
            }

            if (this.containsCampaignFields) { return 'unlayerBuilder.saveAsTemplate.alert.campaign.fields'; }

            if (this.containsCampaignLinks) { return 'unlayerBuilder.saveAsTemplate.alert.campaign.links'; }

            return '';
        },
    },

    methods: {
        handleSubmit() {
            this.status = STATUS_SUBMITTED;

            const { name, editor } = this;

            if (name.trim() === '') {
                return;
            }

            editor.exportHtml(({ design, html }) => {
                this.save({
                    name,
                    design,
                    html,
                });
            });
        },

        async save(template) {
            this.status = STATUS_SAVING;

            try {
                await axios.post('/app/unlayerEmailFunnel/saveAsTemplate', {
                    title: template.name,
                    html: template.html,
                    design: JSON.stringify(template.design),
                    funnelId: Number(this.funnelId),
                    templateId: this.templateId,
                });

                this.status = 'SAVED';
                this.$toast({
                    message: `Your template, ${template.name}, has been saved.`,
                });
                this.$emit('saved');
            } catch (e) {
                sentry.captureException(e);
                this.status = STATUS_ERROR;
            }
        },

        focusNameInput() {
            const { nameInput } = this.$refs;

            if (nameInput) {
                this.$nextTick(() => {
                    nameInput.input_focus();
                });
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.alert-container {
    margin-top: $spacing-200;
}

.fields-links-alert {
    margin-bottom: $spacing-200;
}
</style>
